import React, { useEffect, useMemo, useState } from 'react'
import { useDiagnosticsSummary } from '../hooks/use-diagnostics-summary'
import { DiagnosticFinding, Severity } from '../types'
import { FindingGroup } from './finding-group'
import { SummaryBar } from './summary-bar'

interface DiagnosticsDrawerProps {
  findings: DiagnosticFinding[]
  onClose: () => void
}

const SEVERITY_ORDER: Record<Severity, number> = {
  high: 0,
  warning: 1,
  info: 2,
}

interface GroupedFindings {
  ruleId: string
  findings: DiagnosticFinding[]
  maxSeverity: number
}

const groupAndSort = (findings: DiagnosticFinding[]): GroupedFindings[] => {
  const groups = new Map<string, DiagnosticFinding[]>()

  const sorted = [...findings].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
  )

  for (const finding of sorted) {
    const existing = groups.get(finding.ruleId)
    if (existing) {
      existing.push(finding)
    } else {
      groups.set(finding.ruleId, [finding])
    }
  }

  return Array.from(groups.entries())
    .map(([ruleId, findings]) => ({
      ruleId,
      findings,
      maxSeverity: Math.min(...findings.map(f => SEVERITY_ORDER[f.severity])),
    }))
    .sort((a, b) => a.maxSeverity - b.maxSeverity)
}

export const DiagnosticsDrawer = ({
  findings,
  onClose,
}: DiagnosticsDrawerProps) => {
  const summary = useDiagnosticsSummary(findings)
  const [activeFilter, setActiveFilter] = useState<Severity | null>(null)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const filteredFindings = useMemo(() => {
    if (!activeFilter) {
      return findings
    }
    return findings.filter(f => f.severity === activeFilter)
  }, [findings, activeFilter])

  const grouped = useMemo(
    () => groupAndSort(filteredFindings),
    [filteredFindings],
  )

  const handleFilterToggle = (severity: Severity) => {
    setActiveFilter(prev => (prev === severity ? null : severity))
  }

  return (
    <div
      className="inset-x-0 bottom-0 z-[9998] flex max-h-80 flex-col border-gray-200 border-t bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.1)]"
      style={{ position: 'fixed' }} // NOTE: fixed classname is overwritten by common/utils/GlobalStyles.js (62)
    >
      <div className="flex shrink-0 items-center justify-between border-gray-200 border-b bg-gray-50 px-4 py-2">
        <div className="flex items-center gap-3">
          <span className="font-semibold text-gray-700 text-sm">
            Page Diagnostics
          </span>
          <span className="text-gray-500 text-xs">
            {filteredFindings.length}
            {activeFilter ? ` of ${findings.length}` : ''} finding
            {filteredFindings.length !== 1 ? 's' : ''}
          </span>
        </div>
        <button
          type="button"
          className="cursor-pointer border-none bg-transparent px-2 py-1 text-[13px] text-gray-500"
          onClick={onClose}
          title="Close (Esc)"
        >
          Close
        </button>
      </div>
      {summary.total > 0 && (
        <SummaryBar
          summary={summary}
          activeFilter={activeFilter}
          onFilterToggle={handleFilterToggle}
        />
      )}
      <div className="flex-1 overflow-y-auto">
        {filteredFindings.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-400 text-sm">
            {activeFilter
              ? `No ${activeFilter} findings.`
              : 'No findings — page looks healthy.'}
          </div>
        ) : (
          grouped.map(group => (
            <FindingGroup
              key={group.ruleId}
              ruleId={group.ruleId}
              findings={group.findings}
            />
          ))
        )}
      </div>
    </div>
  )
}
